/**
 * TummyTimeChart Component
 *
 * Displays tummy time progress as a line chart showing:
 * - Total tummy time per session (seconds)
 * - Head-up time per session (seconds)
 * Tracks development over time.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { eventApi } from '../services/api';
import { getBabyKey, decryptEventData } from '../crypto';
import { format, subDays } from 'date-fns';

interface TummyTimeChartProps {
  babyId: string;
}

function fmtSecs(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function TummyTimeChart({ babyId }: TummyTimeChartProps) {
  const [period, setPeriod] = useState<7 | 14 | 30>(14);
  const [sessions, setSessions] = useState<Array<{ date: string; totalSecs: number; headUpSecs: number }>>([]);

  const startDate = subDays(new Date(), period).toISOString();

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['tummy-events', babyId, period],
    queryFn: () => eventApi.list(babyId, { types: 'TUMMY_TIME', startDate, limit: '500' }),
    enabled: !!babyId,
  });

  useEffect(() => {
    async function decrypt() {
      if (!eventsData?.data?.events || !babyId) return;
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) return;

      const results: Array<{ date: string; totalSecs: number; headUpSecs: number }> = [];
      for (const ev of (eventsData.data.events as any[])) {
        try {
          const data = await decryptEventData(ev.encryptedData, aesKey);
          if (data.duration) {
            results.push({
              date: format(new Date(ev.timestamp), 'MM/dd HH:mm'),
              totalSecs: Number(data.duration) || 0,
              headUpSecs: Number(data.headUpDuration) || 0,
            });
          }
        } catch {
          // skip
        }
      }
      // Sort by time
      setSessions(results.reverse());
    }
    decrypt();
  }, [eventsData, babyId]);

  const totalTummyMins = Math.round(sessions.reduce((a, s) => a + s.totalSecs, 0) / 60);
  const avgHeadUpPct = sessions.length > 0
    ? Math.round(sessions.filter(s => s.totalSecs > 0).reduce((a, s) => a + (s.headUpSecs / s.totalSecs) * 100, 0) / sessions.filter(s => s.totalSecs > 0).length)
    : 0;

  return (
    <div className="card p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          🏋️ Tummy Time Progress
        </h3>
        <div className="flex gap-2">
          {([7, 14, 30] as const).map(d => (
            <button key={d} onClick={() => setPeriod(d)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                period === d
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p className="text-3xl mb-2">🏋️</p>
          <p>No tummy time logged in the last {period} days</p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{sessions.length}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sessions</div>
            </div>
            <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-teal-700 dark:text-teal-300">{totalTummyMins}m</div>
              <div className="text-xs text-teal-500 dark:text-teal-400 mt-1">Total time</div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{avgHeadUpPct}%</div>
              <div className="text-xs text-yellow-500 dark:text-yellow-400 mt-1">Avg head-up</div>
            </div>
          </div>

          {/* Line chart */}
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={sessions} margin={{ top: 5, right: 5, left: -10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
              <YAxis
                tickFormatter={fmtSecs}
                tick={{ fontSize: 11 }}
                label={{ value: 'MM:SS', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }}
              />
              <Tooltip
                formatter={(val: unknown) => [fmtSecs(Number(val)), '']}
                contentStyle={{ backgroundColor: 'var(--tooltip-bg, #fff)', border: '1px solid #e5e7eb', borderRadius: 8 }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="totalSecs"
                name="Total time"
                stroke="#14b8a6"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="headUpSecs"
                name="Head up time"
                stroke="#eab308"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
            Each point represents one tummy time session
          </p>
        </>
      )}
    </div>
  );
}
